import { get, post } from 'models/shared/helpers/axiosInstance';
import { buildPathWithQueryParams } from 'models/shared/helpers/utils';
import { getPublicStorageBaseUrl } from './publicStorage';
import { isPrivateStorageFile, resolveStorageKey } from './storageVisibility';

export interface PresignedUrlResponse {
	signedUrl: string;
	key: string;
	expiresIn: number;
}

export const isPresignedS3Url = (url?: string) =>
	!!url &&
	(url.includes('X-Amz-Signature=') ||
		url.includes('X-Amz-Algorithm=') ||
		url.includes('AWSAccessKeyId='));

export const needsPresignedS3Url = (url?: string, path?: string) => {
	if (isPresignedS3Url(url)) return false;
	if (url?.trim()) return false;
	return isPrivateStorageFile({ url, path });
};

export const resolvePublicStorageUrl = (file?: {
	url?: string;
	path?: string;
}): string => {
	if (!file) return '';
	if (file.url && !isPresignedS3Url(file.url)) return file.url;
	const key = resolveStorageKey(file);
	if (!key) return file.url || '';
	const base = getPublicStorageBaseUrl();
	return base ? `${base}/${key}` : file.url || '';
};

export const fetchPresignedUrl = async (params: {
	key?: string;
	url?: string;
}): Promise<string> => {
	if (params.key) {
		const res = await get<PresignedUrlResponse>(
			buildPathWithQueryParams('v1/uploads/presigned-url', {
				key: params.key,
			}),
		);
		return res.data.signedUrl;
	}

	const res = await post<PresignedUrlResponse>(
		'v1/uploads/presigned-url',
		params,
	);
	return res.data.signedUrl;
};

export const resolveS3FileUrl = async (file?: {
	url?: string;
	path?: string;
}): Promise<string> => {
	if (!file) return '';

	if (isPresignedS3Url(file.url)) {
		return file.url!;
	}

	if (needsPresignedS3Url(file.url, file.path)) {
		return fetchPresignedUrl({ key: file.path, url: file.url });
	}

	return resolvePublicStorageUrl(file);
};

export const openS3File = async (file?: { url?: string; path?: string }) => {
	const url = await resolveS3FileUrl(file);
	if (url) {
		window.open(url, '_blank');
	}
};
